import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getInitialCode, getInitialXml } from '@/(routers)/boards/editor/block/blockConfig';

export type { BoardType } from '@/(routers)/boards/editor/block/blockConfig';

export interface CodeFile {
    id: string;
    name: string;
    codeContent: string;
    blockContent: string;
    lastSavedCodeContent: string;
    lastSavedBlockContent: string;
    isDirty: boolean;
    isSaving: boolean;
    isSaved: boolean;
    isNewFile: boolean;
    lastModified: string;
    isBlockSynced: boolean;
    isCodeEditConfirmed: boolean;
    preEditBlockContent: string;
}

export interface PreparedSaveData {
    id: string;
    name: string;
    codeContent: string;
    blockContent: string;
    isNewFile: boolean;
}

interface CodeFileState {
    files: CodeFile[];
    currentFileId: string | null;

    setFiles: (files: CodeFile[]) => void;
    addFile: (file: CodeFile) => void;
    updateFile: (id: string, updates: Partial<CodeFile>) => void;
    deleteFile: (id: string) => void;
    deleteFileAndSetCurrent: (deleteId: string, nextId: string) => void;
    setCurrentFile: (id: string | null) => void;
    getCurrentFile: () => CodeFile | null;
    markAsDirty: (id: string) => void;
    prepareSave: (id: string) => PreparedSaveData | null;
    discardChanges: (id: string) => void;
}

export { getInitialCode, getInitialXml };

export const useBoardEditorFileStore = create<CodeFileState>()(
    persist(
        (set, get) => ({
            files: [],
            currentFileId: null,

            setFiles: (files) => set({ files }),
            addFile: (file) => set((state) => ({ files: [file, ...state.files] })),
            updateFile: (id, updates) =>
                set((state) => ({
                    files: state.files.map((f) => (f.id === id ? { ...f, ...updates } : f)),
                })),
            deleteFile: (id) =>
                set((state) => ({
                    files: state.files.filter((f) => f.id !== id),
                    currentFileId: state.currentFileId === id ? null : state.currentFileId,
                })),
            deleteFileAndSetCurrent: (deleteId, nextId) =>
                set((state) => ({
                    files: state.files.filter((f) => f.id !== deleteId),
                    currentFileId: nextId,
                })),
            setCurrentFile: (id) => set({ currentFileId: id }),
            getCurrentFile: () => {
                const state = get();
                return state.files.find((f) => f.id === state.currentFileId) || null;
            },
            markAsDirty: (id) =>
                set((state) => ({
                    files: state.files.map((f) => (f.id === id ? { ...f, isDirty: true } : f)),
                })),
            prepareSave: (id) => {
                const f = get().files.find((file) => file.id === id);
                if (!f) return null;

                let blockContent = f.blockContent;
                try {
                    const dom = new DOMParser().parseFromString(
                        f.blockContent || '<xml></xml>',
                        'text/xml',
                    ).documentElement;
                    dom.setAttribute('isBlockSynced', String(f.isBlockSynced ?? true));
                    dom.setAttribute('preEditBlockContent', f.preEditBlockContent ?? '');
                    blockContent = new XMLSerializer().serializeToString(dom);
                } catch {}

                set((state) => ({
                    files: state.files.map((file) =>
                        file.id !== id
                            ? file
                            : {
                                  ...file,
                                  isDirty: false,
                                  isSaved: true,
                                  blockContent,
                              },
                    ),
                }));

                return {
                    id: f.id,
                    name: f.name,
                    codeContent: f.codeContent,
                    blockContent,
                    isNewFile: f.isNewFile,
                };
            },
            discardChanges: (id) =>
                set((state) => ({
                    files: state.files.map((f) =>
                        f.id === id
                            ? {
                                  ...f,
                                  codeContent: f.lastSavedCodeContent,
                                  blockContent: f.lastSavedBlockContent,
                                  isDirty: false,
                                  isBlockSynced: true,
                                  isCodeEditConfirmed: false,
                                  preEditBlockContent: '',
                              }
                            : f,
                    ),
                })),
        }),
        {
            name: 'KEY-BOARD-EDITOR-FILE-STRAGE',
            storage: createJSONStorage(() => localStorage),
            version: 1,
            migrate: (persistedState: any) => {
                persistedState?.files?.forEach((f: any) => {
                    f.lastModified ??= new Date().toISOString();
                });
                return persistedState;
            },
        },
    ),
);
